import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { getLoanStatuses } from 'src/pages/LoansPage/LoanStatusInfoMap';
import { loanTypeOptions } from 'src/pages/LoansPage/LoanTypesMap';
import { LoansFilter, LoansStore } from 'src/stores/LoansStore/LoansStore';
import { DateHelper } from 'src/utils/DateHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const LoansHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <SearchPanel
            inputSearchFormName={formNames.getPath('filterSearch')}
            store={store}
            renderPanel={(props) => <FilterPanel {...props} />}
            inputSearchPlaceholder={t(
                'Page.Loans.AsideHeader.Search.Placeholder',
            )}
        />
    );
});

export const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getLoanStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            onFinish={(values: FilterWithDateRange) => {
                store.pager.reset();
                store.filterCriteria?.applyFilter(
                    convertFormValuesToFilterCriteria(values),
                );
                store.loadList();
            }}
        >
            <StyledFormItem
                label={t('Page.Loans.Filter.Type')}
                name={formNames.getPath('filterLoanTypes')}
            >
                <Select
                    allowClear
                    mode="multiple"
                    data-testid="filterLoanTypes"
                >
                    {loanTypeOptions.map(({ id, title }) => (
                        <Select.Option
                            value={id}
                            key={id}
                            data-testid={`loans-type-option-${id}`}
                        >
                            {t(`data:loanType.${title}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledFormItem>
            <StyledFormItem
                label={t('Page.Loans.Filter.Status')}
                name={formNames.getPath('filterStatuses')}
            >
                <Select allowClear mode="multiple" data-testid="filterStatuses">
                    {statuses.map((item) => (
                        <Select.Option
                            value={item.id}
                            key={item.id}
                            data-testid={item.id}
                        >
                            {t(`data:status.loan.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledFormItem>
            <Form.Item
                label={t('Page.Loans.Filter.IssuedAt')}
                name={formNames.getPath(dateRangeName)}
            >
                <DateRangePicker
                    placeholder={[
                        t('Component.DateRangePicker.Start'),
                        t('Component.DateRangePicker.End'),
                    ]}
                    data-testid={dateRangeName}
                />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const store = LoansStore;
const formNames = FormHelper.createNameProvider<FilterWithDateRange>();
const dateRangeName = 'filterIssueDate';

type FilterWithDateRange = Omit<
    LoansFilter,
    'filterIssueDateFrom' | 'filterIssueDateTo'
> & {
    [dateRangeName]: [moment.Moment, moment.Moment];
};

const convertFormValuesToFilterCriteria = (
    values: FilterWithDateRange,
): LoansFilter => {
    const { filterIssueDate, filterStatuses, filterLoanTypes, ...otherValues } =
        values;
    const [filterIssueDateFrom, filterIssueDateTo] = filterIssueDate || [
        undefined,
        undefined,
    ];

    return {
        ...otherValues,
        filterStatuses:
            filterStatuses && filterStatuses.length > 0
                ? filterStatuses
                : undefined,
        filterLoanTypes:
            filterLoanTypes && filterLoanTypes.length > 0
                ? filterLoanTypes
                : undefined,
        filterIssueDateFrom: filterIssueDateFrom
            ? DateHelper.formatToISO(filterIssueDateFrom.startOf('day'))
            : undefined,
        filterIssueDateTo: filterIssueDateTo
            ? DateHelper.formatToISO(filterIssueDateTo.endOf('day'))
            : undefined,
    };
};

const StyledFormItem = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: auto;
        border-radius: 0.5rem;
    }
`;
